import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import {
  Activity, FileText, Pill, Stethoscope, Loader2,
  Brain, AlertCircle, RefreshCw, Heart, Droplets,
  Zap, Microscope, FlaskConical, ShieldAlert, Sun,
  ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus, Trash2,
} from 'lucide-react';
import { medicalAPI } from '../services/api';
import { toast } from '../hooks/use-toast';

// ====== System metadata ======
const SYSTEM_META = {
  cardio:        { label: 'Сердечно-сосудистая',   icon: Heart,        color: 'text-red-400',     bg: 'bg-red-500/10' },
  blood:         { label: 'Кровь',                  icon: Droplets,     color: 'text-rose-400',    bg: 'bg-rose-500/10' },
  liver:         { label: 'Печень',                 icon: FlaskConical, color: 'text-amber-400',   bg: 'bg-amber-500/10' },
  kidney:        { label: 'Почки',                  icon: Microscope,   color: 'text-blue-400',    bg: 'bg-blue-500/10' },
  endocrine:     { label: 'Эндокринная',            icon: Zap,          color: 'text-violet-400',  bg: 'bg-violet-500/10' },
  inflammation:  { label: 'Воспаление / Иммунитет', icon: ShieldAlert,  color: 'text-orange-400',  bg: 'bg-orange-500/10' },
  vitamins:      { label: 'Витамины и минералы',    icon: Sun,          color: 'text-yellow-400',  bg: 'bg-yellow-500/10' },
  metabolism:    { label: 'Метаболизм',             icon: Activity,     color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  lipid:         { label: 'Липидный профиль',       icon: Droplets,     color: 'text-pink-400',    bg: 'bg-pink-500/10' },
  other:         { label: 'Прочие показатели',      icon: FileText,     color: 'text-white/70',    bg: 'bg-white/5' },
};

const STATUS_META = {
  normal:         { label: 'Норма',     color: 'text-green-400',  ring: 'ring-green-400/40' },
  high:           { label: 'Повышен',   color: 'text-orange-400', ring: 'ring-orange-400/40' },
  low:            { label: 'Понижен',   color: 'text-yellow-400', ring: 'ring-yellow-400/40' },
  critical_high:  { label: 'Критически высокий', color: 'text-red-500', ring: 'ring-red-500/60' },
  critical_low:   { label: 'Критически низкий',  color: 'text-red-500', ring: 'ring-red-500/60' },
};

const PERIODS = [
  { value: 30,   label: '30 дней' },
  { value: 90,   label: '90 дней' },
  { value: 365,  label: '1 год' },
  { value: 3650, label: 'Всё' },
];

// ====== Sparkline ======
const Sparkline = ({ history, statusColor }) => {
  if (!history || history.length === 0) return null;
  // Single measurement — show a single dot to indicate "first value", not "no history"
  if (history.length === 1) {
    return (
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-8">
        <circle cx="50" cy="50" r="4" fill="currentColor"
                className={statusColor || 'text-white/60'}
                vectorEffect="non-scaling-stroke" />
      </svg>
    );
  }
  const values = history.map(p => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = history.map((p, i) => {
    const x = (i / (history.length - 1)) * 100;
    const y = 100 - ((p.value - min) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-8">
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        vectorEffect="non-scaling-stroke"
        className={statusColor || 'text-white/60'}
      />
      {history.map((p, i) => {
        const x = (i / (history.length - 1)) * 100;
        const y = 100 - ((p.value - min) / range) * 100;
        return (
          <circle key={i} cx={x} cy={y} r="2" fill="currentColor"
                  className={statusColor || 'text-white/60'}
                  vectorEffect="non-scaling-stroke" />
        );
      })}
    </svg>
  );
};

// ====== Metric Card ======
const MetricCard = ({ metric }) => {
  const status = STATUS_META[metric.status] || STATUS_META.normal;
  const trend = metric.history && metric.history.length >= 2
    ? metric.history[metric.history.length - 1].value - metric.history[0].value
    : 0;
  const TrendIcon = trend > 0 ? TrendingUp : trend < 0 ? TrendingDown : Minus;
  const trendColor = trend === 0 ? 'text-white/40'
    : (metric.status === 'normal' ? 'text-white/60' : (trend > 0 ? 'text-orange-400' : 'text-blue-400'));

  return (
    <div
      className={`liquid-glass-card p-4 space-y-2 ring-1 ${status.ring}`}
      data-testid={`metric-card-${metric.name_normalized}`}
    >
      <div className="relative z-10 flex items-start justify-between gap-2">
        <p className="text-white/80 text-xs sm:text-sm font-medium leading-tight">{metric.name}</p>
        {metric.count > 1 && (
          <span className="text-white/40 text-[10px] uppercase tracking-wider whitespace-nowrap">
            {metric.count} зам.
          </span>
        )}
      </div>
      <div className="relative z-10 flex items-baseline gap-1.5">
        <span className="text-2xl sm:text-3xl font-bold text-white">
          {Number.isInteger(metric.latest_value) ? metric.latest_value : metric.latest_value.toFixed(2)}
        </span>
        <span className="text-white/50 text-xs">{metric.unit}</span>
      </div>
      <div className="relative z-10 flex items-center justify-between">
        <span className={`text-xs font-medium ${status.color}`}>{status.label}</span>
        {metric.count > 1 && (
          <span className={`flex items-center gap-0.5 text-[11px] ${trendColor}`}>
            <TrendIcon className="w-3 h-3" />
            {trend !== 0 && (Number.isInteger(trend) ? trend : trend.toFixed(2))}
          </span>
        )}
      </div>
      {metric.ref_range && (
        <p className="relative z-10 text-white/40 text-[10px]">Норма: {metric.ref_range}</p>
      )}
      <div className={`relative z-10 ${status.color}`}>
        <Sparkline history={metric.history} statusColor={status.color} />
      </div>
    </div>
  );
};

// ====== System Group ======
const SystemGroup = ({ systemKey, metrics, defaultOpen = true }) => {
  const meta = SYSTEM_META[systemKey] || SYSTEM_META.other;
  const Icon = meta.icon;
  const [open, setOpen] = useState(defaultOpen);
  const hasAlerts = metrics.some(m =>
    ['high', 'low', 'critical_high', 'critical_low'].includes(m.status)
  );

  return (
    <div className="liquid-glass-card p-4 sm:p-5" data-testid={`system-group-${systemKey}`}>
      <button
        onClick={() => setOpen(!open)}
        className="relative z-10 w-full flex items-center justify-between gap-3"
        data-testid={`system-toggle-${systemKey}`}
      >
        <div className="flex items-center gap-3">
          <div className={`liquid-glass-icon w-10 h-10 flex items-center justify-center ${meta.bg}`}>
            <Icon className={`w-5 h-5 ${meta.color}`} />
          </div>
          <div className="text-left">
            <h3 className="text-white text-base sm:text-lg font-medium">{meta.label}</h3>
            <p className="text-white/50 text-xs">
              {metrics.length} показател{metrics.length === 1 ? 'ь' : metrics.length < 5 ? 'я' : 'ей'}
              {hasAlerts && <span className="ml-2 text-orange-400">• есть отклонения</span>}
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="text-white/60 w-5 h-5" /> : <ChevronDown className="text-white/60 w-5 h-5" />}
      </button>
      {open && (
        <div className="relative z-10 mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {metrics.map(m => <MetricCard key={m.name_normalized} metric={m} />)}
        </div>
      )}
    </div>
  );
};

// ====== Main Component ======
const HealthStatsTab = () => {
  const [stats, setStats] = useState(null);
  const [grouped, setGrouped] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [period, setPeriod] = useState(90);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = async (days = period) => {
    setRefreshing(true);
    try {
      const [statsData, groupedData] = await Promise.all([
        medicalAPI.getHealthStats(),
        medicalAPI.getHealthMetricsGrouped(days),
      ]);
      setStats(statsData);
      setGrouped(groupedData);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast({ title: 'Ошибка', description: 'Не удалось загрузить статистику', variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { loadData(period); /* eslint-disable-next-line */ }, [period]);

  const loadAnalysis = async () => {
    setAnalysisLoading(true);
    try {
      const data = await medicalAPI.getHealthAnalysis();
      setAnalysis(data);
      toast({ title: 'Анализ готов', description: 'AI провёл комплексный анализ' });
    } catch (error) {
      toast({ title: 'Ошибка', description: 'Не удалось получить анализ', variant: 'destructive' });
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleClearMetrics = async () => {
    if (!window.confirm('Удалить все извлечённые показатели? Это действие необратимо.')) return;
    try {
      const res = await medicalAPI.deleteAllHealthMetrics();
      toast({ title: 'Удалено', description: `Удалено ${res.deleted} записей` });
      loadData();
    } catch (e) {
      toast({ title: 'Ошибка', variant: 'destructive' });
    }
  };

  const sortedSystems = useMemo(() => {
    if (!grouped?.systems) return [];
    const order = ['cardio', 'blood', 'lipid', 'metabolism', 'liver', 'kidney',
                   'endocrine', 'inflammation', 'vitamins', 'other'];
    return order
      .filter(k => grouped.systems[k]?.length > 0)
      .map(k => ({ key: k, metrics: grouped.systems[k] }));
  }, [grouped]);

  const totalMetrics = grouped?.total_metrics || 0;
  const hasNoMetrics = !loading && totalMetrics === 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-white/60" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="health-stats-tab">
      {/* Header + Period */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-light text-white">Показатели здоровья</h2>
          <p className="text-white/50 text-sm">
            Извлечено из чата: {totalMetrics} показател{totalMetrics === 1 ? 'ь' : totalMetrics < 5 ? 'я' : 'ей'}
            {stats?.chat_metrics_unique > 0 && ` (${stats.chat_metrics_unique} уникальных)`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="period-selector">
          {PERIODS.map(p => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              data-testid={`period-${p.value}`}
              className={`px-3 py-1.5 rounded-full text-xs sm:text-sm transition-all border ${
                period === p.value
                  ? 'bg-white/20 border-white/40 text-white'
                  : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
              }`}
            >{p.label}</button>
          ))}
        </div>
      </div>

      {/* AI Analysis CTA */}
      <div className="liquid-glass-card p-5 sm:p-6">
        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="liquid-glass-icon p-3"><Brain size={24} className="text-white" /></div>
            <div>
              <h3 className="text-lg font-medium text-white">AI Анализ здоровья</h3>
              <p className="text-white/60 text-sm">Комплексная оценка на основе ваших данных</p>
            </div>
          </div>
          <Button
            onClick={loadAnalysis}
            disabled={analysisLoading}
            className="bg-white/15 hover:bg-white/25 text-white border border-white/20 rounded-full w-full sm:w-auto"
            data-testid="get-analysis-btn"
          >
            {analysisLoading
              ? <><Loader2 className="mr-2 animate-spin" size={16} />Анализирую...</>
              : <><RefreshCw className="mr-2" size={16} />{analysis ? 'Обновить' : 'Получить анализ'}</>}
          </Button>
        </div>
      </div>

      {/* Empty state — no metrics from chat yet */}
      {hasNoMetrics && (
        <div className="liquid-glass-card p-8 text-center" data-testid="no-metrics-state">
          <AlertCircle className="mx-auto mb-3 text-white/40 relative z-10" size={48} />
          <p className="relative z-10 text-white/80 mb-2 font-medium">Показатели появятся здесь автоматически</p>
          <p className="relative z-10 text-white/50 text-sm">
            Загрузите медицинские документы (анализы, УЗИ, ЭКГ) в чат — AI извлечёт все показатели
            и сгруппирует их по системам организма с динамикой изменений.
          </p>
        </div>
      )}

      {/* Dynamic system groups */}
      {sortedSystems.length > 0 && (
        <div className="space-y-4" data-testid="system-groups">
          {sortedSystems.map(({ key, metrics }, idx) => (
            <SystemGroup key={key} systemKey={key} metrics={metrics} defaultOpen={idx < 3} />
          ))}
        </div>
      )}

      {/* Summary records (medical_records collection) */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="liquid-glass-card p-4 sm:p-5" data-testid="summary-total-records">
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <p className="text-white/70 text-xs sm:text-sm mb-1">Всего записей</p>
                <p className="text-white text-2xl sm:text-3xl font-bold">{stats.total_records}</p>
              </div>
              <div className="liquid-glass-icon w-12 h-12 flex items-center justify-center">
                <FileText className="text-blue-400" size={24} />
              </div>
            </div>
          </div>
          <div className="liquid-glass-card p-4 sm:p-5" data-testid="summary-consultations">
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <p className="text-white/70 text-xs sm:text-sm mb-1">Консультации</p>
                <p className="text-white text-2xl sm:text-3xl font-bold">{stats.consultations}</p>
              </div>
              <div className="liquid-glass-icon w-12 h-12 flex items-center justify-center">
                <Stethoscope className="text-green-400" size={24} />
              </div>
            </div>
          </div>
          <div className="liquid-glass-card p-4 sm:p-5" data-testid="summary-analyses">
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <p className="text-white/70 text-xs sm:text-sm mb-1">Анализы</p>
                <p className="text-white text-2xl sm:text-3xl font-bold">{stats.analyses}</p>
              </div>
              <div className="liquid-glass-icon w-12 h-12 flex items-center justify-center">
                <Activity className="text-purple-400" size={24} />
              </div>
            </div>
          </div>
          <div className="liquid-glass-card p-4 sm:p-5" data-testid="summary-prescriptions">
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <p className="text-white/70 text-xs sm:text-sm mb-1">Рецепты</p>
                <p className="text-white text-2xl sm:text-3xl font-bold">{stats.prescriptions}</p>
              </div>
              <div className="liquid-glass-icon w-12 h-12 flex items-center justify-center">
                <Pill className="text-pink-400" size={24} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Result */}
      {analysis && (
        <div className="liquid-glass-card p-4 sm:p-6" data-testid="ai-analysis-result">
          <div className="relative z-10 flex items-center gap-3 mb-4">
            <div className="liquid-glass-icon w-10 h-10 flex items-center justify-center">
              <Brain className="text-blue-400" size={22} />
            </div>
            <h3 className="text-white text-base sm:text-lg font-medium">Результат AI анализа</h3>
          </div>
          <div className="relative z-10 prose prose-invert max-w-none">
            <div className="text-white/90 text-sm sm:text-base whitespace-pre-wrap leading-relaxed">
              {analysis.analysis}
            </div>
          </div>
          <div className="relative z-10 mt-4 pt-4 border-t border-white/10">
            <p className="text-white/50 text-xs">
              Анализ создан: {new Date(analysis.generated_at).toLocaleString('ru-RU')}
            </p>
          </div>
        </div>
      )}

      {/* Recent Diagnoses */}
      {stats?.recent_diagnoses && stats.recent_diagnoses.length > 0 && (
        <div className="liquid-glass-card p-4 sm:p-6" data-testid="recent-diagnoses">
          <h3 className="relative z-10 text-white text-base sm:text-lg font-medium mb-4 flex items-center gap-2">
            <div className="liquid-glass-icon w-8 h-8 flex items-center justify-center">
              <FileText size={18} className="text-white" />
            </div>
            Последние диагнозы
          </h3>
          <div className="relative z-10 flex flex-wrap gap-2">
            {stats.recent_diagnoses.map((d, i) => (
              <span key={i} className="px-3 py-1.5 bg-blue-500/20 border border-blue-400/30 text-blue-200 rounded-full text-xs sm:text-sm">
                {d}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Footer note + clear */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4">
        <p className="text-center text-white/40 text-xs">
          * Показатели извлекаются автоматически из ваших чатов с медицинскими документами
        </p>
        {totalMetrics > 0 && (
          <button
            onClick={handleClearMetrics}
            data-testid="clear-metrics-btn"
            className="flex items-center gap-1.5 text-white/40 hover:text-red-400 text-xs transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Очистить все показатели
          </button>
        )}
      </div>

      {refreshing && (
        <div className="fixed bottom-6 right-6 liquid-glass-card px-4 py-2 flex items-center gap-2 z-50">
          <Loader2 className="animate-spin text-white/60" size={16} />
          <span className="text-white/70 text-sm">Обновление...</span>
        </div>
      )}
    </div>
  );
};

export default HealthStatsTab;
